/**
 * 通知をタップしたときの遷移先（PRODUCT_DEV-808）
 * ============================================================================
 *
 * **遷移先は `type` ではなく `metadata` のキーで決まる。** 同じ分岐が
 * 通知一覧のタップ（web `pages/Notifications/index.tsx`）と Web Push の URL
 * （通知 worker `buildPushUrl`）の2か所にあり、以前はそれぞれに if が並んでいた。
 * 実際に `inquiryId` は web にだけ足されて食い違っていた。片方だけ足すと
 * 「プッシュを踏んだ人だけ一覧に落ちる」ので、ここ1本にまとめる。
 *
 * 戻り値はアプリ内のパス（`BASE_URL` を含まない）。web は `path()` を通して遷移し、
 * worker はそのまま Push の URL にする。遷移先が無ければ null。
 *
 * ⚠ **判定の順序は既存のまま**（商談 → グループ → メモ → 問い合わせ）。新しいキーは後ろに足す。
 */

/** 日報・週報・月報の画面（`/mypage/reports/:period/:periodKey`）へ行くためのキー。 */
export const NOTIFICATION_METADATA_REPORT_PERIOD = "reportPeriod";
export const NOTIFICATION_METADATA_REPORT_PERIOD_KEY = "reportPeriodKey";
/** 部のまとめ（管理者ビュー `/mypage/admin`）へ行くためのキー。値は期間キー。 */
export const NOTIFICATION_METADATA_TEAM_REPORT_PERIOD_KEY = "teamReportPeriodKey";
/**
 * 部のまとめの種類（daily / weekly / monthly）と、その期間の基準日（`YYYY-MM-DD`。期間の末日）。
 * PRODUCT_DEV-721 U6: あれば管理者ビューをその期間で開く（リンクは部署と期間を持つ。指摘10）。
 * ⚠ 種類のキーは本人の日報の `reportPeriod` と**名前を分けてある**（同じ名前だと本人の日報の画面へ飛ぶ）。
 */
export const NOTIFICATION_METADATA_TEAM_REPORT_PERIOD = "teamReportPeriod";
export const NOTIFICATION_METADATA_TEAM_REPORT_ANCHOR = "teamReportAnchor";
/** 管理者ビューを期間つきで開くクエリ名（`teamReportPath` が付け、管理者ビューが読む）。 */
export const TEAM_REPORT_PERIOD_QUERY_KEY = "period";
export const TEAM_REPORT_ANCHOR_QUERY_KEY = "anchor";
/**
 * PRODUCT_DEV-721 U6: どの部署の日報・部のまとめか。
 * あれば遷移先に `?deptId=` を付ける（開いた画面が「いまの部署」をその部署へ切り替える）。
 */
export const NOTIFICATION_METADATA_REPORT_DEPT_ID = "reportDeptId";
/**
 * PRODUCT_DEV-721 U6: 日報・部のまとめの画面と API が部署を受け取るクエリ名。リンクを作る側（メール・通知）、
 * 画面（`useReportDept`）、API（`reportDeptIdOfQuery`）が全部この名前を使う（片方だけ変えるとリンクが黙って切れる）。
 */
export const REPORT_DEPT_QUERY_KEY = "deptId";
/** 日程調整の詳細（`/mypage/scheduling/:scheduleId`）へ行くためのキー。 */
export const NOTIFICATION_METADATA_SCHEDULE_ID = "scheduleId";

const seg = (value: string): string => encodeURIComponent(value);

/** 日報・部のまとめへのパスに部署を付ける（PRODUCT_DEV-721 U6）。部署が無ければそのまま。 */
export const withReportDept = (path: string, deptId: string | null | undefined): string =>
  deptId ? `${path}${path.includes("?") ? "&" : "?"}${REPORT_DEPT_QUERY_KEY}=${seg(deptId)}` : path;

/**
 * 部のまとめ（管理者ビュー）へのパス。部署と期間を持つ（PRODUCT_DEV-721 U6・指摘10）。
 * 種類と基準日のどちらかが無ければ期間は付けない（管理者ビューが開いた直後の期間をサーバに選ばせる）。
 */
export const teamReportPath = (input: {
  deptId?: string | null;
  period?: string | null;
  anchor?: string | null;
}): string => {
  const params: string[] = [];
  if (input.period && input.anchor) {
    params.push(`${TEAM_REPORT_PERIOD_QUERY_KEY}=${seg(input.period)}`);
    params.push(`${TEAM_REPORT_ANCHOR_QUERY_KEY}=${seg(input.anchor)}`);
  }
  if (input.deptId) params.push(`${REPORT_DEPT_QUERY_KEY}=${seg(input.deptId)}`);
  return params.length > 0 ? `/mypage/admin?${params.join("&")}` : "/mypage/admin";
};

export const notificationPath = (
  metadata: Record<string, string> | undefined,
): string | null => {
  if (!metadata) return null;
  if (metadata.meetingId) return `/meetings/${seg(metadata.meetingId)}`;
  if (metadata.groupId) return `/meeting-groups/${seg(metadata.groupId)}`;
  // メモ通知 (memo_completed / memo_failed)。failed でも詳細で失敗表示まで出る
  if (metadata.memoId) return `/memo/${seg(metadata.memoId)}`;
  // 問い合わせ回答 (PRODUCT_DEV-635)
  if (metadata.inquiryId) return `/inquiries/${seg(metadata.inquiryId)}`;
  // PRODUCT_DEV-808: 日報（下書き・コメント）
  const period = metadata[NOTIFICATION_METADATA_REPORT_PERIOD];
  const periodKey = metadata[NOTIFICATION_METADATA_REPORT_PERIOD_KEY];
  const reportDeptId = metadata[NOTIFICATION_METADATA_REPORT_DEPT_ID];
  if (period && periodKey) {
    return withReportDept(`/mypage/reports/${seg(period)}/${seg(periodKey)}`, reportDeptId);
  }
  // PRODUCT_DEV-808: 部のまとめ
  if (metadata[NOTIFICATION_METADATA_TEAM_REPORT_PERIOD_KEY]) {
    return teamReportPath({
      deptId: reportDeptId,
      period: metadata[NOTIFICATION_METADATA_TEAM_REPORT_PERIOD],
      anchor: metadata[NOTIFICATION_METADATA_TEAM_REPORT_ANCHOR],
    });
  }
  // PRODUCT_DEV-808: 日程調整
  const scheduleId = metadata[NOTIFICATION_METADATA_SCHEDULE_ID];
  if (scheduleId) return `/mypage/scheduling/${seg(scheduleId)}`;
  return null;
};
